import React from "react";
import { useRouter } from "next/router";
import { Form, Formik, FormikHelpers } from "formik";
import { AxiosError } from "axios";
import Box from "@mui/material/Box";
import { useSnackbar } from "notistack";

import { enrollButtonStyles } from "../../../bootcamps/components/detail/common/EnrollButton";
import OutlinedInputWithStyles from "../../../common/components/form/input/OutlinedInputWithStyles";
import { getErrorMessage } from "../../../common/utils/error";
import SpinnerButton from "@packages/common/src/components/form/SpinnerButton";
import { signFormValidationSchema } from "../../../common/components/form/validations";
import GoogleOAuthButton from "../../../common/components/form/auth/GoogleOAuthButton";
import OrSeparator from "../../../common/components/layout/utils/OrSeparator";
import FormCaption from "../../../bootcamps/components/detail/enroll-section/FormCaption";
import { pushDataToGTM } from "../../../common/utils/utils";
import useUserContext from "../../../common/context/get-user/useUserContext";

const EnrollForm: React.FC<{
  enrollUrl: string;
}> = ({ enrollUrl }) => {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { setIsLoaded } = useUserContext();

  const initialState = {
    email: "",
    username: "",
    password: "",
  };

  const handleSubmit = async (
    data: {
      email: string;
      username: string;
      password: string;
    },
    actions: FormikHelpers<any>
  ) => {
    try {
      const coursesService = new (
        await import("../../service/coursesService")
      ).default();

      const { email, username, password } = data;
      const response = await coursesService.enroll(enrollUrl, {
        email,
        username,
        password,
      });

      if (response) {
        pushDataToGTM({
          event: "signup",
          page: "course_enroll",
          userId: response.data.user_id,
        });
        actions.setSubmitting(false);
        router.replace(router.asPath);
        // fetch user data
        setIsLoaded(false);
      }
    } catch (error) {
      actions.setSubmitting(false);
      enqueueSnackbar(getErrorMessage((error as AxiosError)?.response?.data), {
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        variant: "error",
      });
    }
  };

  return (
    <>
      <GoogleOAuthButton redirectUrl={`${router.asPath}?enroll=true`}>
        Enroll With Google
      </GoogleOAuthButton>
      <OrSeparator />
      <Box width={{ xs: "85%", md: "80%" }}>
        <Formik
          initialValues={initialState}
          validationSchema={signFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting }) => (
            <Form>
              <Box sx={{ display: "grid", rowGap: 4 }}>
                <OutlinedInputWithStyles
                  type="email"
                  name="email"
                  value={values.email}
                  textFieldProps={{ label: "Email", "aria-label": "Email" }}
                />
                <OutlinedInputWithStyles
                  type="text"
                  name="username"
                  value={values.username}
                  textFieldProps={{
                    label: "Username",
                    "aria-label": "username",
                  }}
                />
                <OutlinedInputWithStyles
                  type="password"
                  name="password"
                  value={values.password}
                  textFieldProps={{
                    label: "Password",
                    "aria-label": "password",
                    autoComplete: "new-password",
                  }}
                />
                <Box py={2} display="flex" justifyContent={"center"}>
                  <SpinnerButton
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    sx={enrollButtonStyles}
                  >
                    Enroll For Free
                  </SpinnerButton>
                </Box>
                <FormCaption />
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default EnrollForm;
